<template>
  <v-container fluid>
    <div class="toolbar d-flex flex-row justify-space-between align-center elevation-3 mb-3">
      <span>
        Separate Products
      </span>
      <div
        class="d-flex flex-row align-center"
        style="gap: 0.625rem;">
        <v-btn
          icon
          color="secondary"
          :disabled="loading"
          @click="initOnCreate()">
          <v-icon :class="{ 'mdi-spin': loading }">
            mdi-cached
          </v-icon>
        </v-btn>
        <template v-if="movingState === moveState.IDLE">
          <v-btn
            :to="{
              name: 'MoveProducts',
              query: {
                noteCode: note.code,
                autoDeliveryNoteId: note.id
              }
            }"
            target="_blank"
            color="secondary"
            outlined
            rounded
            small
            :loading="loading">
            {{ $t('button.Histories') }}
          </v-btn>
          <v-btn
            color="success"
            rounded
            small
            :loading="loading"
            :disabled="disableNextButton"
            @click="gotoMoveProducts()">
            {{ $t('button.Next') }}
          </v-btn>
        </template>
        <v-btn
          v-else-if="movingState === moveState.MOVING"
          color="secondary"
          rounded
          small
          :loading="loading"
          @click="onBack()">
          {{ $t('button.Back') }}
        </v-btn>
        <v-btn
          v-else
          to="/inventory/delivery-notes"
          color="secondary"
          rounded
          small
          :loading="loading">
          {{ $t('button.Back') }}
        </v-btn>
      </div>
    </div>
    <v-row v-if="movingState === moveState.IDLE">
      <v-col
        cols="12"
        md="6">
        <v-card
          class="card-container elevation-3"
          :loading="loading">
          <table class="pt-5">
            <tr>
              <td class="title">
                เลขที่ใบนำเข้า
              </td>
              <td>{{ note.code }}</td>
            </tr>
            <tr>
              <td class="title">
                จำนวนสินค้า
              </td>
              <td>{{ total.product | showNumberFormat() }} แบบ</td>
            </tr>
            <tr>
              <td class="title">
                จำนวนชิ้น
              </td>
              <td>{{ total.count | showNumberFormat() }} ชิ้น</td>
            </tr>
            <tr>
              <td class="title">
                วันที่ครบกำหนด
              </td>
              <td>
                <v-text-field
                  v-model="dueDate"
                  type="date"
                  :min="minDueDate"
                  :disabled="disableDueDate"
                  placeholder="เลือกวันครบกำหนด"
                  color="secondary"
                  dense
                  hide-details
                  clearable
                  style="max-width: 220px;"
                  @input="onInputDueDate($event)" />
              </td>
            </tr>
          </table>
        </v-card>
        <v-card
          class="card-container elevation-3"
          :loading="loading">
          <div class="title pt-5">
            เลือก Preset เพื่อแสดงรายการสาขา
          </div>
          <v-radio-group
            v-model="selectedPreset"
            hide-details>
            <div
              v-for="preset in separatePresets"
              :key="preset.id"
              class="separate-preset d-flex justify-space-between align-center"
              @click="onSelectPreset(preset)">
              <span>
                {{ preset.name }}
              </span>
              <v-radio
                :value="preset.id"
                color="success"
                readonly />
            </div>
          </v-radio-group>
        </v-card>
      </v-col>
      <v-col
        cols="12"
        md="6">
        <v-card
          class="card-container elevation-3"
          :loading="loading">
          <div class="d-flex justify-space-between align-center pt-5">
            <div class="title">
              เลือกสาขาที่ต้องการนำเข้า
            </div>
            <v-btn
              v-if="selectedPreset"
              color="success"
              plain
              :loading="loading"
              @click="onAddWarehouse()">
              <v-icon>mdi-plus</v-icon>
              Add
            </v-btn>
          </div>
          <v-data-table
            v-if="selectedPreset"
            v-model="selectedBranches"
            :headers="headers"
            :items="branches"
            :items-per-page="-1"
            :loading="loading"
            show-select
            item-key="id"
            fixed-header
            hide-default-footer>
            <template #[`item.name`]="{ item, index }">
              <WarehouseSelect
                v-if="item.isNew"
                v-model="branches[index]"
                :items="filterWarehouses"
                item-text="name"
                item-value="id"
                :menu-props="{ offsetY: true }"
                :rules="[
                  (v) => !!v || 'Warehouse is required'
                ]"
                label="Warehouse"
                outlined
                return-object
                style="max-width: 280px;"
                @select="onSelectWarehouse($event, index)" />
              <span v-else>
                {{ item.name }}
              </span>
            </template>
            <template #[`item.percent`]="{ item, index }">
              <gw-text-field
                v-model.number="branches[index].percent"
                type="number"
                :rules="{
                  required: true,
                  min_value: 0,
                  decimal: 3
                }"
                prepend-inner-icon="mdi-percent-outline"
                append-icon="mdi-delete"
                color="secondary"
                dense
                hide-details
                :loading="loading"
                @click:append="onRemoveWarehouse(index)" />
            </template>
          </v-data-table>
          <span v-else>
            โปรดเลือก Preset เพื่อแสดงรายการสาขาที่ต้องการนำเข้า
          </span>
        </v-card>
      </v-col>
    </v-row>
    <v-row v-else-if="movingState === moveState.MOVING">
      <v-col cols="12">
        <v-card
          class="card-container-no-bg elevation-0"
          :loading="loading">
          <separate-products
            :note="note"
            :selected-branches="selectedBranches"
            :warehouse-id="warehouseId"
            :total="total"
            :due-date="dueDate"
            @summary="setupSummaryNotes($event)" />
        </v-card>
      </v-col>
    </v-row>
    <v-row v-else>
      <v-col cols="12">
        <v-card
          class="card-container elevation-3"
          :loading="loading || autoDeliveryNoteLoading">
          <summary-move-products
            v-if="summaryNotes.length && !(loading || autoDeliveryNoteLoading)"
            :note-code="note.code"
            :summary-notes="summaryNotes"
            :on-approved-wn="onApprovedWn"
          />
          <div
            v-else-if="!(loading || autoDeliveryNoteLoading)"
            class="pt-8">
            ไม่พบข้อมูลการกระจายสินค้า
          </div>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { mapActions } from 'vuex'
import WarehouseProvider from '@/resources/WarehouseProvider'
import InventoryProvider from '@/resources/InventoryProvider'
import AutoDeliveryNotesProvider from '@/resources/AutoDeliveryNotesProvider'
import StoreProvider from '@/resources/StoreProvider'
import WarehouseSelect from '@/components/WarehouseSelect.vue'
import SeparateProducts from '../components/SeparateProducts.vue'
import SummaryMoveProducts from '../components/SummaryMoveProducts.vue'
import ProductsSeparatePresetsProvider from '@/resources/ProductsSeparatePresetsProvider'
import { getRole } from '@/assets/js/Authentication'

const WarehouseService = new WarehouseProvider()
const InventoryService = new InventoryProvider()
const AutoDeliveryNotesService = new AutoDeliveryNotesProvider()
const StoreService = new StoreProvider()
const ProductsSeparatePresetsService = new ProductsSeparatePresetsProvider()

export default {
  components: {
    WarehouseSelect,
    SeparateProducts,
    SummaryMoveProducts
  },
  data () {
    return {
      headers: [
        {
          text: 'สาขา',
          value: 'name'
        },
        {
          text: 'เปอร์เซนต์',
          value: 'percent',
          align: 'center',
          width: '150px'
        }
      ],
      branches: [],
      loading: true,
      autoDeliveryNoteLoading: true,
      deliveryNoteId: null,
      warehouseId: null,
      note: {
        code: ''
      },
      total: {
        product: 0,
        amount: 0,
        count: 0
      },
      moveState: {
        IDLE: 'idle',
        MOVING: 'moving',
        SUMMARY: 'summary'
      },
      movingState: 'idle',
      dueDate: null,
      summaryNotes: [],
      summaryNotesStatus: [],
      separatePresets: [],
      selectedPreset: null
    }
  },
  computed: {
    disableDueDate () {
      const allowRole = [
        'management',
        'inventory_and_costing',
        'developer',
        'merchandising_planner',
        'merchandiser',
        'accounting_manager',
        'accounting',
        'support',
        'warehouse_manager'
      ]
      const role = getRole()
      return !allowRole.some((r) => r === role)
    },
    warehouses () {
      return this.$store.getters['Store/mapWarehouse']?.filter((v) => v.id !== 0) || []
    },
    filterWarehouses () {
      return this.warehouses.map((warehouse) => ({
        ...warehouse,
        disabled: !!this.branches.find((item) => item.id === warehouse.id)
      }))
    },
    selectedBranches: {
      get () {
        return this.branches.filter((v) => v.selected)
      },
      set (val) {
        const selectedIds = val.map((v) => v.id)

        this.branches = this.branches.map((v) => ({
          ...v,
          selected: selectedIds.includes(v.id)
        }))
      }
    },
    disableNextButton () {
      return !this.selectedBranches.length
        || this.selectedBranches.some((v) => !v.id || v.percent === '' || v.percent === null || v.percent === undefined)
    },
    autoDeliveryNoteId () {
      return 'autoDeliveryNoteId' in this.$route.query ? Number(this.$route.query.autoDeliveryNoteId) : null
    },
    minDueDate () {
      return this.$dayjs().format('YYYY-MM-DD')
    }
  },
  created () {
    this.initOnCreate()
  },
  methods: {
    ...mapActions({
      setSnackbar: 'Components/setSnackbar'
    }),
    async initOnCreate () {
      if (this.$route?.query?.noteCode && this.$route?.query?.noteCode !== '') {
        this.note = { code: this.$route.query.noteCode }
      }
      this.loading = true
      try {
        await Promise.all([
          this.initDeliveryNote(),
          this.initMoveProductsPresets()
        ])
      } finally {
        this.loading = false
      }
      this.initAutoDeliveryNote()
    },
    onInputDueDate (val) {
      if (val && !this.$dayjs(val).isSameOrAfter(this.minDueDate, 'day')) {
        this.setSnackbar({
          value: true,
          message: 'วันครบกำหนดต้องมากกว่าหรือเท่ากับวันนี้',
          type: 'error'
        })
        this.dueDate = this.minDueDate
        return
      }
      this.dueDate = val || null
    },
    onSelectPreset (item) {
      if (this.selectedPreset !== item.id) {
        this.selectedPreset = item.id
        this.branches = [...item.warehouses].map((v) => ({
          ...v,
          selected: true
        }))
        this.selectedBranches = [...this.branches]
      }
    },
    onSelectWarehouse (val, index) {
      if (val) {
        this.selectedBranches = []
        this.branches[index] = {
          id: val.id,
          code: val.code,
          name: val.name,
          percent: this.branches[index]?.percent || 0,
          isNew: true,
          selected: false
        }
      }
    },
    onAddWarehouse () {
      this.selectedBranches = []
      this.branches.push({
        id: null,
        code: null,
        name: null,
        percent: 0,
        isNew: true
      })
    },
    onRemoveWarehouse (index) {
      const branch = this.branches[index]
      const selectIndex = this.selectedBranches.findIndex((b) => b.id === branch.id)

      if (selectIndex !== -1) {
        this.selectedBranches.splice(selectIndex, 1)
      }

      this.branches.splice(index, 1)
    },
    gotoMoveProducts () {
      if (this.selectedBranches.length !== this.branches.length) {
        const totalPercent = this.selectedBranches.reduce((sum, b) => sum + (+b.percent || 0), 0)
        this.selectedBranches = this.selectedBranches.map((branch) => ({
          ...branch,
          percent: totalPercent > 0 ? +((branch.percent / totalPercent) * 100).toFixed(2) : 0
        }))
      } else {
        this.selectedBranches = this.branches.map((v) => ({ ...v, selected: true }))
      }
      this.movingState = this.moveState.MOVING
    },
    onBack () {
      this.movingState = this.moveState.IDLE
    },
    async initAutoDeliveryNote () {
      try {
        this.autoDeliveryNoteLoading = true

        if (this.autoDeliveryNoteId) {
          this.movingState = this.moveState.SUMMARY
          const { data } = await AutoDeliveryNotesService.getAutoDeliveryNoteById(this.autoDeliveryNoteId)

          await this.setupSummaryNotes(data)
        }
      } catch (error) {
        console.error('initAutoDeliveryNote', error)
        this.setSnackbar({
          value: true,
          message: `Error code ${error.code} : ${error.message}`,
          type: 'error'
        })
      } finally {
        this.autoDeliveryNoteLoading = false
      }
    },
    async initDeliveryNote () {
      try {
        this.warehouseId = this.$route.query?.warehouseId || null
        this.deliveryNoteId = this.$route.query?.noteId || null
        if (this.warehouseId && this.deliveryNoteId) {
          this.warehouseId = +this.warehouseId

          const { data } = await InventoryService.getDeliveryNoteById(this.deliveryNoteId)

          const groupProducts = data.products.reduce((obj, p) => {
            const tmpObj = { ...obj }

            if (!(p.id in tmpObj)) {
              tmpObj[p.id] = {
                ...p,
                skus: []
              }
            }

            p.skus.forEach((s) => {
              const skuIndex = tmpObj[p.id].skus.findIndex(
                (i) => i.code === s.code
              )

              if (skuIndex === -1) {
                tmpObj[p.id].skus.push(s)
              } else {
                tmpObj[p.id].skus[skuIndex].amount += s.amount
                tmpObj[p.id].skus[skuIndex].count += s.count
              }
            })

            return tmpObj
          }, {})

          this.note = {
            ...data,
            products: Object.values(groupProducts)
          }

          const amountProducts = this.note.products.length
          const amountSkusMapped = this.note.products.reduce((acc, product) => {
            const { skus } = product

            return acc + skus.reduce((sum, currentSkus) => sum + currentSkus.amount, 0)
          }, 0)

          const countSkusMapped = this.note.products.reduce((acc, product) => {
            const { skus } = product

            return acc + skus.reduce((sum, currentSkus) => sum + currentSkus.count, 0)
          }, 0)

          this.total = {
            product: amountProducts,
            amount: amountSkusMapped,
            count: countSkusMapped
          }
        }
      } catch (error) {
        console.error('initDeliveryNote', error)
        this.setSnackbar({
          value: true,
          message: `Error code ${error.code} : ${error.message}`,
          type: 'error'
        })
      }
    },
    async getMoveStockWarehouse () {
      const { data: { moveStockWarehouse } } = await StoreService.getStoreSetting()
      return moveStockWarehouse
    },
    async initBranches () {
      try {
        this.loading = true

        const availableWarehouses = await this.getMoveStockWarehouse()

        if (this.note) {
          const { data: branches } = await WarehouseService.getWarehouse()

          const availableBranchesId = availableWarehouses.map((b) => b.id)
          this.branches = branches.filter((branch) => availableBranchesId.includes(branch.id) && branch.id !== this.warehouseId)
          this.branches = this.branches.map((branch) => {
            const tmpBranch = branch
            const { percent } = availableWarehouses.find((b) => b.id === branch.id)
            tmpBranch.percent = percent

            return tmpBranch
          })
          this.branches = this.branches.sort((a, b) => b.percent - a.percent)
        }
      } catch (error) {
        console.error('initBranches', error)
        this.setSnackbar({
          value: true,
          message: `Error code ${error.code} : ${error.message}`,
          type: 'error'
        })
      } finally {
        this.loading = false
      }
    },
    async initMoveProductsPresets () {
      try {
        const { data } = await ProductsSeparatePresetsService.getItems()
        this.separatePresets = data
      } catch (error) {
        console.error('initMoveProductsPresets', error)
        this.setSnackbar({
          value: true,
          message: `Error code ${error.code} : ${error.message}`,
          type: 'error'
        })
      }
    },
    async setupSummaryNotes (notes) {
      this.summaryNotes = await this.updateNotes(notes)
      this.movingState = this.moveState.SUMMARY
    },
    async updateNotes (notes) {
      try {
        const updatedNotes = await Promise.all(
          notes.map(async (note) => ({
            ...note,
            toWarehouses: await Promise.all(
              note.toWarehouses.map(async (w) => {
                const withdrawStatus = await InventoryService.getWithdrawNoteById(w.withdrawNote.id)

                // DN จะถูกสร้างหลัง approve WN — ถ้ายังไม่มี DN id ให้ข้ามการดึงสถานะ DN
                let deliveryNote = { ...w.deliveryNote }
                if (w.deliveryNote?.id) {
                  const deliveryStatus = await InventoryService.getDeliveryNoteById(w.deliveryNote.id)
                  deliveryNote = {
                    ...w.deliveryNote,
                    status: deliveryStatus.data.status,
                    warehouseId: deliveryStatus.data.warehouse.id
                  }
                }

                const skuCount = withdrawStatus.data.products.reduce(
                  (productSum, product) => productSum + product.skus.reduce((skuSum, sku) => skuSum + sku.count, 0), 0
                )

                const skuCountIsComplete = withdrawStatus.data.products.reduce(
                  (productSum, product) => productSum
                    + product.skus.reduce((skuSum, sku) => skuSum + (sku.count === sku.amount ? sku.count : 0), 0), 0
                )

                const withdrawNote = {
                  ...w.withdrawNote,
                  status: withdrawStatus.data.status,
                  warehouseId: withdrawStatus.data.warehouse.id,
                  products: withdrawStatus.data.products
                }

                return {
                  ...w,
                  deliveryNote,
                  withdrawNote,
                  skuCount,
                  skuCountIsComplete
                }
              })
            )
          }))
        )
        return updatedNotes
      } catch (error) {
        console.error('updateNotes', error)
        this.setSnackbar({
          value: true,
          message: `Error code ${error.code} : ${error.message}`,
          type: 'error'
        })
        return []
      }
    },
    async onApprovedWn (withdrawNoteId) {
      const summaryIndex = this.summaryNotes.findIndex((summary) => summary.toWarehouses.some(
        (toWarehouse) => toWarehouse.withdrawNote.id === withdrawNoteId
      ))
      if (summaryIndex !== -1) {
        const toWarehouseIndex = this.summaryNotes[summaryIndex].toWarehouses.findIndex(
          (toWarehouse) => toWarehouse.withdrawNote.id === withdrawNoteId
        )
        if (toWarehouseIndex !== -1) {
          this.summaryNotes[summaryIndex].toWarehouses[toWarehouseIndex].withdrawNote.status = 'approved'
        }
      }
    }
  }
}
</script>

<style>
.v-sheet.theme--light.v-toolbar.v-toolbar--dense.v-app-bar.v-app-bar--fixed {
  z-index: 10 !important;
}
</style>

<style scoped>
.title {
  font-weight: 600;
}
table {
  min-width: 400px;
}
td {
  padding: 5px 2px;
}
.checkbox {
  margin: 0 !important;
  padding: 0 0 0 10% !important;
}
.toolbar {
  position: sticky;
  top: 70px;
  left: 0;
  width: 100%;
  z-index: 6;
  background-color: #fff;
  padding: 1.25rem 1.875rem;
  border-radius: 0.625rem;
}
.toolbar span {
  font-weight: bold;
  font-size: 1.25rem;
}
.card-container {
  padding: 0 1.25rem 1.875rem 1.25rem;
  background-color: #fff;
  border-radius: 0.625rem;
  margin-top: 1.25rem;
}
.card-container-no-bg {
  padding: 0 1.25rem 1.875rem 1.25rem;
  background: transparent !important;
  border-radius: 0.625rem;
  margin-top: 1.25rem;
}
.card-title {
  font-weight: bold;
  font-size: 1.35rem;
}
.separate-preset {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  background-color: #e9e9e9;
  border-radius: 10px;

  padding: 10px;
  margin-top: 10px;
  width: 100%;

  cursor: pointer;
}
</style>
